/**
 * Quick search over the catalog tree, run in the browser against the tree the
 * page already holds. The result is a narrowed tree in catalog order plus the
 * matched characters per row, so the tree can show why a row was kept.
 */
import fuzzysort from "fuzzysort";
import type { TreeTopic } from "../api/domain.ts";
import { type Anchor, anchorId } from "./anchor.ts";

/** Which characters of a row's title the query hit, keyed by the row's element id. */
export type Marks = ReadonlyMap<string, readonly number[]>;

export type CatalogSearch = {
	/** The tree narrowed to what matched, in catalog order. */
	topics: TreeTopic[];
	marks: Marks;
};

/** One searchable row: the text a reader sees, and the row it belongs to. */
type Node = { anchor: Anchor; text: string };

/**
 * Below the library's default of 0.5, because a quick search should forgive a
 * typo or a missing word; the highlight shows what the match rests on.
 */
const threshold = 0.3;

const nodesOf = (topics: readonly TreeTopic[]): Node[] =>
	topics.flatMap((topic) => [
		{ anchor: { kind: "topic", id: topic.id }, text: topic.title },
		...topic.capabilities.map(
			(capability): Node => ({
				anchor: { kind: "capability", id: capability.id },
				text: capability.label,
			}),
		),
		...topic.questions.map(
			(question): Node => ({
				anchor: { kind: "question", id: question.id },
				text: question.title,
			}),
		),
	]);

/**
 * Narrows the tree to the query. A topic stays when its own title matches or
 * any row under it does. Under a topic kept only for its title, everything
 * stays, because the title is what the reader was after and its contents are
 * the answer. Otherwise only the rows that matched stay, so a hit deep in a
 * long topic is not buried among its neighbours.
 *
 * An empty query returns the tree untouched, with no marks.
 */
export const searchCatalog = (
	topics: readonly TreeTopic[],
	query: string,
): CatalogSearch => {
	const trimmed = query.trim();
	if (trimmed === "") return { topics: [...topics], marks: new Map() };

	const hits = fuzzysort.go(trimmed, nodesOf(topics), {
		key: "text",
		limit: 0,
		threshold,
	});
	const marks = new Map<string, readonly number[]>(
		hits.map((hit) => [anchorId(hit.obj.anchor), hit.indexes]),
	);
	const hit = (anchor: Anchor) => marks.has(anchorId(anchor));

	const narrowed = topics.flatMap((topic): TreeTopic[] => {
		const capabilities = topic.capabilities.filter((capability) =>
			hit({ kind: "capability", id: capability.id }),
		);
		const questions = topic.questions.filter((question) =>
			hit({ kind: "question", id: question.id }),
		);
		const inside = capabilities.length > 0 || questions.length > 0;
		if (inside) return [{ ...topic, capabilities, questions }];
		return hit({ kind: "topic", id: topic.id }) ? [topic] : [];
	});

	return { topics: narrowed, marks };
};

/** A stretch of a title, either matched by the query or not. */
export type Run = { text: string; hit: boolean };

/**
 * Splits a title into runs so the matched characters can be wrapped. Adjacent
 * hits share one run, which keeps a matched word as one mark rather than one
 * per letter.
 */
export const runsOf = (text: string, indexes: readonly number[]): Run[] => {
	const marked = new Set(indexes);
	const runs: Run[] = [];
	for (let at = 0; at < text.length; at += 1) {
		const hit = marked.has(at);
		const last = runs[runs.length - 1];
		if (last !== undefined && last.hit === hit) last.text += text[at];
		else runs.push({ text: text[at] ?? "", hit });
	}
	return runs;
};
